import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ClipboardCheck, Plus, FileText, Download, Trash2, Upload, Image, ChevronDown, ChevronUp, Edit, Eye } from 'lucide-react';

const API_URL = '';

// TSIR Categories
const TSIR_CATEGORIES = [
  { value: 'Residential-New', label: 'Residential - New' },
  { value: 'Residential-Renovation', label: 'Residential - Renovation' },
  { value: 'Restaurant-Fit-out', label: 'Restaurant - Fit-out' },
  { value: 'Restaurant-Renovation', label: 'Restaurant - Renovation' },
];

// Initial form state for TSIR
const getInitialFormState = () => ({
  project_id: '',
  category: '',
  project_location: '',
  inspection_date: '',
  inspection_time: '',
  lead_architect: '',
  
  // Restaurant-specific Project Info
  lease_space: '',
  gross_leasable_area: '',
  landlord: '',
  
  // Residential-specific Project Info
  client_representative: '',
  unit_type: '',
  floor_area: '',
  
  // Restaurant Architectural & Structural
  arch_slab_flooring: '',
  arch_waterproofing: '',
  arch_demising_walls: '',
  arch_ceiling: '',
  arch_doors_egress: '',
  arch_accessibility: '',
  arch_facade: '',
  
  // Residential Architectural & Structural
  res_arch_slab_flooring: '',
  res_arch_interior_walls: '',
  res_arch_ceiling: '',
  res_arch_roofing: '',
  res_arch_windows: '',
  res_arch_doors: '',
  res_arch_balcony: '',
  res_arch_storage: '',
  res_arch_wet_area: '',
  
  // Restaurant MEPF
  mepf_exhaust: '',
  mepf_hvac: '',
  mepf_electrical_power: '',
  mepf_electrical_systems: '',
  mepf_plumbing_water: '',
  mepf_plumbing_sanitary: '',
  mepf_grease_trap: '',
  mepf_gas: '',
  mepf_fire_sprinkler: '',
  mepf_fire_fdas: '',
  
  // Residential MEPF
  res_mepf_main_panel: '',
  res_mepf_outlets: '',
  res_mepf_lighting: '',
  res_mepf_ac_provision: '',
  res_mepf_water_supply: '',
  res_mepf_drainage: '',
  res_mepf_hot_water: '',
  res_mepf_ventilation: '',
  res_mepf_fire_protection: '',
  res_mepf_communications: '',
  
  // Residential Room-by-Room
  res_room_living_dining: '',
  res_room_kitchen: '',
  res_room_master_bedroom: '',
  res_room_bedroom2: '',
  res_room_bedroom3: '',
  res_room_master_bathroom: '',
  res_room_common_bathroom: '',
  res_room_service_area: '',
  res_room_maids_room: '',
  
  // Restaurant Renovation & Salvage
  salvage_demolition: '',
  salvage_utility: '',
  salvage_electrical: '',
  salvage_hazardous: '',
  salvage_odor: '',
  salvage_fire_safety: '',
  salvage_pest_control: '',
  salvage_entry_points: '',
  salvage_nesting: '',
  salvage_drainage: '',
  salvage_adjacent_risk: '',
  
  // Residential Renovation & Salvage
  res_salvage_flooring: '',
  res_salvage_walls: '',
  res_salvage_ceiling: '',
  res_salvage_fixtures: '',
  res_salvage_cabinetry: '',
  res_salvage_electrical: '',
  res_salvage_plumbing: '',
  res_salvage_hazardous: '',
  res_salvage_pest: '',
  res_salvage_structural: '',
  
  // Residential Condo Requirements (for Renovation)
  res_condo_fitout_guidelines: '',
  res_condo_working_hours: '',
  res_condo_contractor_accreditation: '',
  res_condo_permits: '',
  res_condo_deposit_bond: '',
  res_condo_material_delivery: '',
  res_condo_debris_disposal: '',
  res_condo_neighbor_notification: '',
  
  // Residential-New Site Information
  new_client_owner: '',
  new_lot_area: '',
  new_building_type: '',
  
  // Site Characteristics
  new_site_lot_dimensions: '',
  new_site_lot_orientation: '',
  new_site_topography: '',
  new_site_soil_condition: '',
  new_site_drainage: '',
  new_site_vegetation: '',
  new_site_existing_structures: '',
  new_site_boundary_markers: '',
  
  // Site Context & Surroundings
  new_context_road_access: '',
  new_context_road_frontage: '',
  new_context_adjacent_left: '',
  new_context_adjacent_right: '',
  new_context_adjacent_rear: '',
  new_context_neighborhood: '',
  new_context_flood_risk: '',
  new_context_environmental: '',
  
  // Utility Availability
  new_utility_electrical: '',
  new_utility_water: '',
  new_utility_sewer: '',
  new_utility_storm_drainage: '',
  new_utility_telecom: '',
  new_utility_gas: '',
  
  // Regulatory & Zoning
  new_zoning_classification: '',
  new_zoning_front_setback: '',
  new_zoning_side_setback: '',
  new_zoning_rear_setback: '',
  new_zoning_max_height: '',
  new_zoning_lot_coverage: '',
  new_zoning_firewall: '',
  new_zoning_easements: '',
  new_zoning_hoa: '',
  
  // Construction Logistics
  new_logistics_delivery_access: '',
  new_logistics_equipment_access: '',
  new_logistics_temp_utilities: '',
  new_logistics_staging_area: '',
  new_logistics_security: '',
  new_logistics_neighbor_coord: '',
  new_logistics_working_hours: '',
  new_logistics_debris_disposal: '',
  
  // Residential-New Documents
  new_doc_lot_title: 'N/A',
  new_doc_tax_declaration: 'N/A',
  new_doc_survey_plan: 'N/A',
  new_doc_zoning_cert: 'N/A',
  new_doc_locational_clearance: 'N/A',
  new_doc_hoa_guidelines: 'N/A',
  new_doc_soil_report: 'N/A',
  new_doc_utility_letter: 'N/A',
  
  // Other
  other_findings: '',
  
  // Restaurant Documents
  doc_design_guidelines: 'N/A',
  doc_construction_guidelines: 'N/A',
  doc_unit_cutup_plan: 'N/A',
  doc_asbuilt_drawings: 'N/A',
  doc_occupancy_permit: 'N/A',
  doc_working_hours: 'N/A',
  doc_structural_signoff: 'N/A',
  
  // Residential Documents
  res_doc_floor_plan: 'N/A',
  res_doc_asbuilt_mepf: 'N/A',
  res_doc_fitout_guidelines: 'N/A',
  res_doc_title_cct: 'N/A',
  res_doc_association_clearance: 'N/A',
  res_doc_renovation_records: 'N/A',
  
  // Final
  final_recommendation: '',
  conditional_issues: '',
  site_photos: [],
});

export default function TSIRTab({ user, projects }) {
  const [tsirList, setTsirList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(getInitialFormState());
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    project: true,
    architectural: false,
    mepf: false,
    salvage: false,
    other: false,
    documents: false,
    final: false,
    // Residential-Renovation sections
    res_architectural: false,
    res_mepf: false,
    res_rooms: false,
    res_salvage: false,
    res_condo: false,
    res_documents: false,
    // Residential-New sections
    new_site_characteristics: false,
    new_site_context: false,
    new_utilities: false,
    new_zoning: false,
    new_logistics: false,
    new_documents: false,
  });
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingTsir, setViewingTsir] = useState(null);
  
  useEffect(() => {
    loadTsirList();
  }, []);
  
  const loadTsirList = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tsir`);
      setTsirList(res.data);
    } catch (err) {
      console.error('Failed to load TSIR list:', err);
    }
  };
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };
  
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingPhotos(true);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      
      const res = await axios.post(`${API_URL}/api/tsir/upload-photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.photo_urls && res.data.photo_urls.length > 0) {
        setForm(prev => ({
          ...prev,
          site_photos: [...prev.site_photos, ...res.data.photo_urls]
        }));
        toast.success(`Uploaded ${res.data.photo_urls.length} photo(s)`);
      }
      
      if (res.data.errors && res.data.errors.length > 0) {
        toast.warning(`Some files failed: ${res.data.errors.join(', ')}`);
      }
    } catch (err) {
      toast.error('Failed to upload photos');
      console.error(err);
    } finally {
      setUploadingPhotos(false);
      e.target.value = '';
    }
  };
  
  const removePhoto = (index) => {
    setForm(prev => ({
      ...prev,
      site_photos: prev.site_photos.filter((_, i) => i !== index)
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.project_id) {
      toast.error('Please select a project');
      return;
    }
    if (!form.category) {
      toast.error('Please select a category');
      return;
    }
    
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/tsir/${editingId}`, form);
        toast.success('TSIR updated successfully');
      } else {
        await axios.post(`${API_URL}/api/tsir`, form);
        toast.success('TSIR created successfully');
      }
      
      setShowForm(false);
      setEditingId(null);
      setForm(getInitialFormState());
      loadTsirList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save TSIR');
      console.error(err);
    }
  };
  
  const handleEdit = (tsir) => {
    setForm({
      ...getInitialFormState(),
      ...tsir,
    });
    setEditingId(tsir.id);
    setShowForm(true);
    setExpandedSections({
      project: true,
      architectural: true,
      mepf: true,
      salvage: true,
      other: true,
      documents: true,
      final: true,
    });
  };
  
  const handleView = (tsir) => {
    setViewingTsir(tsir);
    setShowViewDialog(true);
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this TSIR?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/tsir/${id}`);
      toast.success('TSIR deleted');
      loadTsirList();
    } catch (err) {
      toast.error('Failed to delete TSIR');
    }
  };
  
  const handleExportPdf = async (id) => {
    try {
      const response = await axios.get(`${API_URL}/api/tsir/${id}/export/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `TSIR_Report_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exported successfully');
    } catch (err) {
      toast.error('Failed to export PDF');
      console.error(err);
    }
  };
  
  const startNewTsir = () => {
    setForm(getInitialFormState());
    setEditingId(null);
    setShowForm(true);
    setExpandedSections({
      project: true,
      architectural: false,
      mepf: false,
      salvage: false,
      other: false,
      documents: false,
      final: false,
    });
  };
  
  const isRestaurantCategory = form.category?.includes('Restaurant');
  const isResidentialCategory = form.category?.includes('Residential');
  const isResidentialNew = form.category === 'Residential-New';
  const isResidentialRenovation = form.category === 'Residential-Renovation';
  
  const getCategoryBadgeColor = (category) => {
    if (category?.includes('Restaurant')) return 'bg-orange-500/20 text-orange-500 border-orange-500/30';
    if (category?.includes('Residential')) return 'bg-blue-500/20 text-blue-500 border-blue-500/30';
    return 'bg-gray-500/20 text-gray-500 border-gray-500/30';
  };
  
  const getRecommendationBadge = (rec) => {
    if (rec === 'feasible') return 'bg-green-500/20 text-green-500 border-green-500/30';
    if (rec === 'conditional') return 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30';
    if (rec === 'high_risk') return 'bg-red-500/20 text-red-500 border-red-500/30';
    return 'bg-gray-500/20 text-gray-500 border-gray-500/30';
  };
  
  const SectionHeader = ({ title, section, icon: Icon }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-3 bg-primary/10 rounded-lg border border-primary/20 hover:bg-primary/20 transition-colors"
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-primary" />}
        <span className="font-semibold text-foreground">{title}</span>
      </div>
      {expandedSections[section] ? (
        <ChevronUp className="w-4 h-4 text-muted-foreground" />
      ) : (
        <ChevronDown className="w-4 h-4 text-muted-foreground" />
      )}
    </button>
  );
  
  // Render form field inline to prevent focus loss
  const renderFormField = (label, field, placeholder, textarea = false, hint = null) => (
    <div className="space-y-1" key={field}>
      <Label className="text-sm">{label}</Label>
      {textarea ? (
        <Textarea
          value={form[field] || ''}
          onChange={(e) => handleInputChange(field, e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="bg-background/50"
        />
      ) : (
        <Input
          value={form[field] || ''}
          onChange={(e) => handleInputChange(field, e.target.value)}
          placeholder={placeholder}
          className="bg-background/50"
        />
      )}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="glass-effect border-border/40" data-testid="tsir-header-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <ClipboardCheck className="w-5 h-5 text-primary" />
              <CardTitle>Technical Site Inspection Reports (TSIR)</CardTitle>
            </div>
            <Button onClick={startNewTsir} data-testid="btn-new-tsir">
              <Plus className="w-4 h-4 mr-2" />
              New TSIR
            </Button>
          </div>
        </CardHeader>
      </Card>
      
      {/* Form Dialog */}
      {showForm && (
        <Card className="glass-effect border-primary/40 border-2" data-testid="tsir-form-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{editingId ? 'Edit TSIR' : 'New Technical Site Inspection Report'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setEditingId(null); }}>
                Cancel
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Project Selection & Category */}
              <SectionHeader title="Project Information" section="project" icon={FileText} />
              {expandedSections.project && (
                <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Project *</Label>
                      <select
                        value={form.project_id}
                        onChange={(e) => handleInputChange('project_id', e.target.value)}
                        className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm"
                        data-testid="tsir-project-select"
                        required
                      >
                        <option value="">Select Project</option>
                        {projects.map((proj) => (
                          <option key={proj.id} value={proj.id}>
                            {proj.project_name} ({proj.project_code})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Category *</Label>
                      <select
                        value={form.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm"
                        data-testid="tsir-category-select"
                        required
                      >
                        <option value="">Select Category</option>
                        {TSIR_CATEGORIES.map((cat) => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Residential-New Project Info */}
                  {isResidentialNew && (
                    <>
                      <div className="grid md:grid-cols-2 gap-4">
                        {renderFormField("Site Location", "project_location", "Full address / Lot Block details")}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label>Inspection Date</Label>
                            <Input
                              type="date"
                              value={form.inspection_date}
                              onChange={(e) => handleInputChange('inspection_date', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Time</Label>
                            <Input
                              type="time"
                              value={form.inspection_time}
                              onChange={(e) => handleInputChange('inspection_time', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        {renderFormField("Lead Architect/Engineer", "lead_architect", "Name")}
                        {renderFormField("Client/Owner", "new_client_owner", "Name of property owner")}
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        {renderFormField("Lot Area (sqm)", "new_lot_area", "e.g., 150 sqm")}
                        <div>
                          <Label>Proposed Building Type</Label>
                          <select
                            value={form.new_building_type}
                            onChange={(e) => handleInputChange('new_building_type', e.target.value)}
                            className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm"
                          >
                            <option value="">Select Building Type</option>
                            <option value="Single-detached">Single-detached</option>
                            <option value="Duplex">Duplex</option>
                            <option value="Townhouse">Townhouse</option>
                            <option value="Multi-storey">Multi-storey</option>
                          </select>
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Residential-Renovation Project Info */}
                  {isResidentialRenovation && (
                    <>
                      <div className="grid md:grid-cols-2 gap-4">
                        {renderFormField("Project Location", "project_location", "Address / Unit Number")}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label>Inspection Date</Label>
                            <Input
                              type="date"
                              value={form.inspection_date}
                              onChange={(e) => handleInputChange('inspection_date', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Time</Label>
                            <Input
                              type="time"
                              value={form.inspection_time}
                              onChange={(e) => handleInputChange('inspection_time', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        {renderFormField("Lead Architect/Engineer", "lead_architect", "Name")}
                        {renderFormField("Client Representative", "client_representative", "Name of owner/client present")}
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Unit Type</Label>
                          <select
                            value={form.unit_type}
                            onChange={(e) => handleInputChange('unit_type', e.target.value)}
                            className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm"
                          >
                            <option value="">Select Unit Type</option>
                            <option value="Studio">Studio</option>
                            <option value="1BR">1 Bedroom</option>
                            <option value="2BR">2 Bedrooms</option>
                            <option value="3BR">3 Bedrooms</option>
                            <option value="Penthouse">Penthouse</option>
                            <option value="House-Lot">House & Lot</option>
                          </select>
                        </div>
                        {renderFormField("Floor Area (sqm)", "floor_area", "e.g., 85 sqm")}
                      </div>
                    </>
                  )}
                  
                  {isRestaurantCategory && (
                    <>
                      <div className="grid md:grid-cols-2 gap-4">
                        {renderFormField("Project Location", "project_location", "Address / Location")}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label>Inspection Date</Label>
                            <Input
                              type="date"
                              value={form.inspection_date}
                              onChange={(e) => handleInputChange('inspection_date', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Time</Label>
                            <Input
                              type="time"
                              value={form.inspection_time}
                              onChange={(e) => handleInputChange('inspection_time', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        {renderFormField("Lead Architect/Engineer", "lead_architect", "Name")}
                        {renderFormField("Lease Space", "lease_space", "e.g., Ground Floor Unit A")}
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        {renderFormField("Gross Leasable Area (GLA)", "gross_leasable_area", "e.g., 150 sqm")}
                        {renderFormField("Landlord / Property Manager", "landlord", "Name / Company")}
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {/* Restaurant Form Sections */}
              {isRestaurantCategory && (
                <>
                  {/* Architectural & Structural Assessment */}
                  <SectionHeader title="Architectural & Structural Assessment" section="architectural" />
                  {expandedSections.architectural && (
                    <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                      {renderFormField("Slab & Flooring", "arch_slab_flooring", "Condition / Observations", true, "Check: Slab thickness, live load capacity (kPa), FFL variations, substrate condition")}
                      {renderFormField("Waterproofing", "arch_waterproofing", "Condition / Observations", true, "Check: Membrane integrity in kitchens/restrooms, efflorescence, 72-hour flood test")}
                      {renderFormField("Demising Walls", "arch_demising_walls", "Condition / Observations", true, "Check: Material (CHB/drywall), FRR compliance (RA 9514), STC rating")}
                      {renderFormField("Ceiling Infrastructure", "arch_ceiling", "Condition / Observations", true, "Check: Slab-to-slab clearances, hangers, seismic bracing, MEPF routing clearances")}
                      {renderFormField("Doors & Egress", "arch_doors_egress", "Condition / Observations", true, "Check: Fire door ratings (2-hr FRR), intumescent seals, panic hardware, travel distance")}
                      {renderFormField("Accessibility (BP 344)", "arch_accessibility", "Condition / Observations", true, "Check: Ramp gradients (1:12), door widths (0.80m), restroom turning radii (1.50m)")}
                      {renderFormField("Facade & Storefront", "arch_facade", "Condition / Observations", true, "Check: Mullion integrity, wind load limits, glazing specs, signage weight limits")}
                    </div>
                  )}
                  
                  {/* Engineering (MEPF) Assessment */}
                  <SectionHeader title="Engineering (MEPF) Assessment" section="mepf" />
                  {expandedSections.mepf && (
                    <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                      {renderFormField("Mechanical (Exhaust/MUA)", "mepf_exhaust", "Condition / Observations", true, "Check: Kitchen exhaust shaft dimensions, CFM limits, Make-Up Air requirements")}
                      {renderFormField("Mechanical (HVAC)", "mepf_hvac", "Condition / Observations", true, "Check: Cooling provision (Chilled water/VRF/DX), TR capacity, FCU/AHU condition")}
                      {renderFormField("Electrical (Power)", "mepf_electrical_power", "Condition / Observations", true, "Check: Feeder sizes, sub-meter location, panelboard capacity (230V, 3-Phase), spare breakers")}
                      {renderFormField("Electrical (Systems)", "mepf_electrical_systems", "Condition / Observations", true, "Check: Grounding integrity, emergency power allocation from genset")}
                      {renderFormField("Plumbing (Water)", "mepf_plumbing_water", "Condition / Observations", true, "Check: Pipe material (PPR/Copper), static pressure (PSI), sub-meter, shut-off valves")}
                      {renderFormField("Plumbing (Sanitary/Waste)", "mepf_plumbing_sanitary", "Condition / Observations", true, "Check: Invert elevations, waste line slope (1-2%), floor cleanouts")}
                      {renderFormField("Plumbing (Grease Trap)", "mepf_grease_trap", "Condition / Observations", true, "Check: Centralized interceptor vs local SS trap, sizing capacity (GPM)")}
                      {renderFormField("Plumbing (Gas)", "mepf_gas", "Condition / Observations", true, "Check: LPG/LNG piping, regulator condition, pressure testing, leak detection")}
                      {renderFormField("Fire Protection (Sprinkler)", "mepf_fire_sprinkler", "Condition / Observations", true, "Check: Hazard classification, pipe schedule, head coverage, zone valve access")}
                      {renderFormField("Fire Protection (FDAS)", "mepf_fire_fdas", "Condition / Observations", true, "Check: FDAS protocol (addressable/conventional), hood suppression integration")}
                    </div>
                  )}
                  
                  {/* Renovation & Salvage Assessment */}
                  <SectionHeader title="Renovation & Salvage Assessment" section="salvage" />
                  {expandedSections.salvage && (
                    <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                      <div className="grid md:grid-cols-2 gap-4">
                        {renderFormField("Demolition", "salvage_demolition", "Condition / Recommendation", true, "Load-bearing walls hidden in partitions?")}
                        {renderFormField("Utility Salvage", "salvage_utility", "Condition / Recommendation", true, "Can existing AC ducting be reused?")}
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        {renderFormField("Electrical Panelboard", "salvage_electrical", "Condition / Recommendation", true, "Is busbar corroded? Replace if >5 years")}
                        {renderFormField("Hazardous Materials", "salvage_hazardous", "Condition / Recommendation", true, "Evidence of asbestos or lead-based paint?")}
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        {renderFormField("Odor/Sanitation", "salvage_odor", "Condition / Recommendation", true, "Sewer line clogged with old grease?")}
                        {renderFormField("Fire Safety", "salvage_fire_safety", "Condition / Recommendation", true, "Does sprinkler layout meet new plan?")}
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        {renderFormField("Pest Control", "salvage_pest_control", "Condition / Recommendation", true, "Signs: droppings, gnaw marks, carcasses?")}
                        {renderFormField("Entry Points", "salvage_entry_points", "Condition / Recommendation", true, "Unsealed pipe sleeves/wall gaps?")}
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        {renderFormField("Nesting Sites", "salvage_nesting", "Condition / Recommendation", true, "Hollow blocks or old cabinetry?")}
                        {renderFormField("Drainage", "salvage_drainage", "Condition / Recommendation", true, "Floor drains missing P-traps or covers?")}
                      </div>
                      {renderFormField("Adjacent Risk", "salvage_adjacent_risk", "Condition / Recommendation", true, "Do neighboring units have active issues?")}
                    </div>
                  )}
                  
                  {/* Other Findings */}
                  <SectionHeader title="Other Findings and Concerns" section="other" />
                  {expandedSections.other && (
                    <div className="p-4 border border-border/40 rounded-lg">
                      {renderFormField("Additional Notes", "other_findings", "Any other findings, concerns, or observations...", true)}
                    </div>
                  )}
                  
                  {/* Required Documents */}
                  <SectionHeader title="Required Turnover & Admin Handover Documents" section="documents" />
                  {expandedSections.documents && (
                    <div className="space-y-3 p-4 border border-border/40 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-4">Mark each document as Available or N/A</p>
                      {[
                        { field: 'doc_design_guidelines', label: 'Design Guidelines' },
                        { field: 'doc_construction_guidelines', label: 'Construction Guidelines' },
                        { field: 'doc_unit_cutup_plan', label: 'Unit Cut-up Plan' },
                        { field: 'doc_asbuilt_drawings', label: 'As-Built Architectural and MEPF Drawings' },
                        { field: 'doc_occupancy_permit', label: 'Previous Tenant Occupancy Permit / FSIC' },
                        { field: 'doc_working_hours', label: 'Working Hours & Debris Hauling Protocols' },
                        { field: 'doc_structural_signoff', label: 'Structural Sign-off Requirements' },
                      ].map(({ field, label }) => (
                        <div key={field} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
                          <span className="text-sm">{label}</span>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant={form[field] === 'Available' ? 'default' : 'outline'}
                              onClick={() => handleInputChange(field, 'Available')}
                              className="text-xs"
                            >
                              Available
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant={form[field] === 'N/A' ? 'default' : 'outline'}
                              onClick={() => handleInputChange(field, 'N/A')}
                              className="text-xs"
                            >
                              N/A
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Final Recommendation */}
                  <SectionHeader title="Final Recommendation" section="final" />
                  {expandedSections.final && (
                    <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                      <div className="space-y-3">
                        <Label>Select Recommendation</Label>
                        <div className="space-y-2">
                          <label className="flex items-start gap-3 p-3 border border-border/40 rounded-lg cursor-pointer hover:bg-green-500/5 transition-colors">
                            <input
                              type="radio"
                              name="recommendation"
                              value="feasible"
                              checked={form.final_recommendation === 'feasible'}
                              onChange={(e) => handleInputChange('final_recommendation', e.target.value)}
                              className="mt-1"
                            />
                            <div>
                              <span className="font-medium text-green-500">FEASIBLE</span>
                              <p className="text-sm text-muted-foreground">Proceed to Schematic Design</p>
                            </div>
                          </label>
                          <label className="flex items-start gap-3 p-3 border border-border/40 rounded-lg cursor-pointer hover:bg-yellow-500/5 transition-colors">
                            <input
                              type="radio"
                              name="recommendation"
                              value="conditional"
                              checked={form.final_recommendation === 'conditional'}
                              onChange={(e) => handleInputChange('final_recommendation', e.target.value)}
                              className="mt-1"
                            />
                            <div>
                              <span className="font-medium text-yellow-500">CONDITIONAL</span>
                              <p className="text-sm text-muted-foreground">Proceed only if Lessor rectifies issues</p>
                            </div>
                          </label>
                          <label className="flex items-start gap-3 p-3 border border-border/40 rounded-lg cursor-pointer hover:bg-red-500/5 transition-colors">
                            <input
                              type="radio"
                              name="recommendation"
                              value="high_risk"
                              checked={form.final_recommendation === 'high_risk'}
                              onChange={(e) => handleInputChange('final_recommendation', e.target.value)}
                              className="mt-1"
                            />
                            <div>
                              <span className="font-medium text-red-500">HIGH RISK</span>
                              <p className="text-sm text-muted-foreground">Major MEPF deficiencies. Re-evaluate site or renegotiate lease</p>
                            </div>
                          </label>
                        </div>
                      </div>
                      
                      {form.final_recommendation === 'conditional' && (
                        renderFormField("Issues to be Rectified", "conditional_issues", "List the issues that need to be addressed...", true)
                      )}
                    </div>
                  )}
                  
                  {/* Site Photos */}
                  <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2">
                        <Image className="w-4 h-4" />
                        Site Photos
                      </Label>
                      <div>
                        <input
                          type="file"
                          id="tsir-photos"
                          multiple
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('tsir-photos').click()}
                          disabled={uploadingPhotos}
                        >
                          {uploadingPhotos ? (
                            <>Uploading...</>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Add Photos
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    {form.site_photos.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {form.site_photos.map((url, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={url}
                              alt={`Site photo ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg border border-border/40"
                            />
                            <button
                              type="button"
                              onClick={() => removePhoto(index)}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {form.site_photos.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">No photos attached yet</p>
                    )}
                  </div>
                </>
              )}
              
              {/* Residential Form Sections */}
              {isResidentialCategory && (
                <>
                  {/* ========== RESIDENTIAL-NEW SECTIONS ========== */}
                  {isResidentialNew && (
                    <>
                      {/* Site Characteristics */}
                      <SectionHeader title="Site Characteristics" section="new_site_characteristics" />
                      {expandedSections.new_site_characteristics && (
                        <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                          {renderFormField("Lot Dimensions", "new_site_lot_dimensions", "Condition / Observations", true, "Check: Frontage width, depth, irregular shapes, corner lot?")}
                          {renderFormField("Lot Orientation", "new_site_lot_orientation", "Condition / Observations", true, "Check: North/South/East/West facing, sun path considerations")}
                          {renderFormField("Topography", "new_site_topography", "Condition / Observations", true, "Check: Flat, sloped, terraced? Grade difference from road level")}
                          {renderFormField("Soil Condition", "new_site_soil_condition", "Condition / Observations", true, "Check: Soil type (clay/sandy/rocky), evidence of fill material, bearing capacity concerns")}
                          {renderFormField("Drainage Pattern", "new_site_drainage", "Condition / Observations", true, "Check: Natural water flow, low spots, ponding areas, adjacent lot drainage")}
                          {renderFormField("Existing Vegetation", "new_site_vegetation", "Condition / Observations", true, "Check: Trees to retain/remove, landscaping, root systems near build area")}
                          {renderFormField("Existing Structures", "new_site_existing_structures", "Condition / Observations", true, "Check: Old foundations, debris, demolition required?")}
                          {renderFormField("Boundary Markers", "new_site_boundary_markers", "Condition / Observations", true, "Check: Lot pins visible, fence lines, encroachments from/to neighbors")}
                        </div>
                      )}
                      
                      {/* Site Context & Surroundings */}
                      <SectionHeader title="Site Context & Surroundings" section="new_site_context" />
                      {expandedSections.new_site_context && (
                        <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                          {renderFormField("Road Access", "new_context_road_access", "Condition / Observations", true, "Check: Road type (concrete/asphalt/dirt), road width, cul-de-sac?")}
                          {renderFormField("Road Frontage", "new_context_road_frontage", "Condition / Observations", true, "Check: Distance from road to lot, easement/setback from road")}
                          {renderFormField("Adjacent Properties (Left)", "new_context_adjacent_left", "Condition / Observations", true, "Check: Building type, height, setback, windows facing lot")}
                          {renderFormField("Adjacent Properties (Right)", "new_context_adjacent_right", "Condition / Observations", true, "Check: Building type, height, setback, windows facing lot")}
                          {renderFormField("Adjacent Properties (Rear)", "new_context_adjacent_rear", "Condition / Observations", true, "Check: Building type, height, setback, privacy concerns")}
                          {renderFormField("Neighborhood Character", "new_context_neighborhood", "Condition / Observations", true, "Check: Residential density, building styles, noise level")}
                          {renderFormField("Flood Risk", "new_context_flood_risk", "Condition / Observations", true, "Check: Flood zone classification, historical flooding, creek/river proximity")}
                          {renderFormField("Environmental Concerns", "new_context_environmental", "Condition / Observations", true, "Check: Industrial nearby, pollution sources, high voltage lines")}
                        </div>
                      )}
                      
                      {/* Utility Availability */}
                      <SectionHeader title="Utility Availability" section="new_utilities" />
                      {expandedSections.new_utilities && (
                        <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                          {renderFormField("Electrical (Meralco/Local)", "new_utility_electrical", "Availability / Notes", true, "Check: Nearest power line, pole location, transformer distance")}
                          {renderFormField("Water Supply", "new_utility_water", "Availability / Notes", true, "Check: Municipal water main, depth, pipe size, meter location")}
                          {renderFormField("Sanitary Sewer", "new_utility_sewer", "Availability / Notes", true, "Check: Municipal sewer line, septic tank required?, invert depth")}
                          {renderFormField("Storm Drainage", "new_utility_storm_drainage", "Availability / Notes", true, "Check: Catch basin location, drainage canal, outfall")}
                          {renderFormField("Telecommunications", "new_utility_telecom", "Availability / Notes", true, "Check: PLDT/Globe lines, fiber availability, cable TV")}
                          {renderFormField("Natural Gas", "new_utility_gas", "Availability / Notes", true, "Check: LPG delivery or piped gas availability")}
                        </div>
                      )}
                      
                      {/* Regulatory & Zoning Compliance */}
                      <SectionHeader title="Regulatory & Zoning Compliance" section="new_zoning" />
                      {expandedSections.new_zoning && (
                        <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                          <div className="grid md:grid-cols-2 gap-4">
                            {renderFormField("Zoning Classification", "new_zoning_classification", "Data / Notes", true, "Residential (R-1, R-2, R-3), mixed-use?")}
                            {renderFormField("Front Setback Required", "new_zoning_front_setback", "Data / Notes", true, "Minimum distance from road (typically 3-5m)")}
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            {renderFormField("Side Setback Required", "new_zoning_side_setback", "Data / Notes", true, "Minimum distance from side boundaries (typically 2m)")}
                            {renderFormField("Rear Setback Required", "new_zoning_rear_setback", "Data / Notes", true, "Minimum distance from rear boundary (typically 2m)")}
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            {renderFormField("Maximum Building Height", "new_zoning_max_height", "Data / Notes", true, "Number of storeys / meters allowed")}
                            {renderFormField("Maximum Lot Coverage", "new_zoning_lot_coverage", "Data / Notes", true, "Percentage of lot that can be built on (typically 70-80%)")}
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            {renderFormField("Firewall Requirements", "new_zoning_firewall", "Data / Notes", true, "Required at property line? Fire separation distance")}
                            {renderFormField("Easements", "new_zoning_easements", "Data / Notes", true, "Road widening, utility easements, drainage easements")}
                          </div>
                          {renderFormField("HOA Restrictions", "new_zoning_hoa", "Data / Notes", true, "Subdivision design guidelines, architectural control")}
                        </div>
                      )}
                      
                      {/* Construction Logistics */}
                      <SectionHeader title="Construction Logistics" section="new_logistics" />
                      {expandedSections.new_logistics && (
                        <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                          <div className="grid md:grid-cols-2 gap-4">
                            {renderFormField("Material Delivery Access", "new_logistics_delivery_access", "Notes", true, "Truck access, staging area, narrow roads?")}
                            {renderFormField("Equipment Access", "new_logistics_equipment_access", "Notes", true, "Can excavator/crane enter? Overhead obstructions?")}
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            {renderFormField("Temporary Utilities", "new_logistics_temp_utilities", "Notes", true, "Power/water for construction available?")}
                            {renderFormField("Storage/Staging Area", "new_logistics_staging_area", "Notes", true, "Space for materials, worker facilities")}
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            {renderFormField("Security Concerns", "new_logistics_security", "Notes", true, "Gated subdivision, guard, theft risk")}
                            {renderFormField("Neighbor Coordination", "new_logistics_neighbor_coord", "Notes", true, "Party wall agreement needed? Shared driveway?")}
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            {renderFormField("Construction Hours", "new_logistics_working_hours", "Notes", true, "Subdivision rules on working hours")}
                            {renderFormField("Debris Disposal", "new_logistics_debris_disposal", "Notes", true, "Nearest disposal site, hauling route")}
                          </div>
                        </div>
                      )}
                      
                      {/* Other Findings */}
                      <SectionHeader title="Other Findings and Concerns" section="other" />
                      {expandedSections.other && (
                        <div className="p-4 border border-border/40 rounded-lg">
                          {renderFormField("Additional Notes", "other_findings", "Any other findings, concerns, or observations...", true)}
                        </div>
                      )}
                      
                      {/* Required Documents - Residential New */}
                      <SectionHeader title="Required Documents Checklist" section="new_documents" />
                      {expandedSections.new_documents && (
                        <div className="space-y-3 p-4 border border-border/40 rounded-lg">
                          <p className="text-sm text-muted-foreground mb-4">Mark each document as Available or N/A</p>
                          {[
                            { field: 'new_doc_lot_title', label: 'Lot Title (TCT/CCT)' },
                            { field: 'new_doc_tax_declaration', label: 'Tax Declaration' },
                            { field: 'new_doc_survey_plan', label: 'Lot Survey Plan' },
                            { field: 'new_doc_zoning_cert', label: 'Zoning Certificate' },
                            { field: 'new_doc_locational_clearance', label: 'Locational Clearance' },
                            { field: 'new_doc_hoa_guidelines', label: 'Subdivision/HOA Guidelines' },
                            { field: 'new_doc_soil_report', label: 'Soil/Geotechnical Report' },
                            { field: 'new_doc_utility_letter', label: 'Utility Availability Letter' },
                          ].map(({ field, label }) => (
                            <div key={field} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
                              <span className="text-sm">{label}</span>
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant={form[field] === 'Available' ? 'default' : 'outline'}
                                  onClick={() => handleInputChange(field, 'Available')}
                                  className="text-xs"
                                >
                                  Available
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant={form[field] === 'N/A' ? 'default' : 'outline'}
                                  onClick={() => handleInputChange(field, 'N/A')}
                                  className="text-xs"
                                >
                                  N/A
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Final Recommendation - Residential New */}
                      <SectionHeader title="Final Recommendation" section="final" />
                      {expandedSections.final && (
                        <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                          <div className="space-y-3">
                            <Label>Select Recommendation</Label>
                            <div className="space-y-2">
                              <label className="flex items-start gap-3 p-3 border border-border/40 rounded-lg cursor-pointer hover:bg-green-500/5 transition-colors">
                                <input
                                  type="radio"
                                  name="new_recommendation"
                                  value="feasible"
                                  checked={form.final_recommendation === 'feasible'}
                                  onChange={(e) => handleInputChange('final_recommendation', e.target.value)}
                                  className="mt-1"
                                />
                                <div>
                                  <span className="font-medium text-green-500">FEASIBLE FOR CONSTRUCTION</span>
                                  <p className="text-sm text-muted-foreground">Site is suitable with no major issues</p>
                                </div>
                              </label>
                              <label className="flex items-start gap-3 p-3 border border-border/40 rounded-lg cursor-pointer hover:bg-yellow-500/5 transition-colors">
                                <input
                                  type="radio"
                                  name="new_recommendation"
                                  value="conditional"
                                  checked={form.final_recommendation === 'conditional'}
                                  onChange={(e) => handleInputChange('final_recommendation', e.target.value)}
                                  className="mt-1"
                                />
                                <div>
                                  <span className="font-medium text-yellow-500">FEASIBLE WITH CONDITIONS</span>
                                  <p className="text-sm text-muted-foreground">Site is suitable but requires addressing specific concerns</p>
                                </div>
                              </label>
                              <label className="flex items-start gap-3 p-3 border border-border/40 rounded-lg cursor-pointer hover:bg-blue-500/5 transition-colors">
                                <input
                                  type="radio"
                                  name="new_recommendation"
                                  value="requires_study"
                                  checked={form.final_recommendation === 'requires_study'}
                                  onChange={(e) => handleInputChange('final_recommendation', e.target.value)}
                                  className="mt-1"
                                />
                                <div>
                                  <span className="font-medium text-blue-500">REQUIRES FURTHER STUDY</span>
                                  <p className="text-sm text-muted-foreground">Geotechnical/survey/legal issues need resolution first</p>
                                </div>
                              </label>
                              <label className="flex items-start gap-3 p-3 border border-border/40 rounded-lg cursor-pointer hover:bg-red-500/5 transition-colors">
                                <input
                                  type="radio"
                                  name="new_recommendation"
                                  value="not_recommended"
                                  checked={form.final_recommendation === 'not_recommended'}
                                  onChange={(e) => handleInputChange('final_recommendation', e.target.value)}
                                  className="mt-1"
                                />
                                <div>
                                  <span className="font-medium text-red-500">NOT RECOMMENDED</span>
                                  <p className="text-sm text-muted-foreground">Significant issues make construction impractical</p>
                                </div>
                              </label>
                            </div>
                          </div>
                          
                          {(form.final_recommendation === 'conditional' || form.final_recommendation === 'requires_study') && (
                            renderFormField("Issues to be Addressed", "conditional_issues", "Specify issues and required actions...", true)
                          )}
                        </div>
                      )}
                      
                      {/* Site Photos - Residential New */}
                      <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-2">
                            <Image className="w-4 h-4" />
                            Site Photos
                          </Label>
                          <div>
                            <input
                              type="file"
                              id="tsir-photos-new"
                              multiple
                              accept="image/*"
                              onChange={handlePhotoUpload}
                              className="hidden"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => document.getElementById('tsir-photos-new').click()}
                              disabled={uploadingPhotos}
                            >
                              {uploadingPhotos ? (
                                <>Uploading...</>
                              ) : (
                                <>
                                  <Upload className="w-4 h-4 mr-2" />
                                  Add Photos
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">Recommended: Lot frontage, views from all corners, adjacent properties, utility connection points, problem areas</p>
                        
                        {form.site_photos.length > 0 && (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {form.site_photos.map((url, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={url}
                                  alt={`Site photo ${index + 1}`}
                                  className="w-full h-24 object-cover rounded-lg border border-border/40"
                                />
                                <button
                                  type="button"
                                  onClick={() => removePhoto(index)}
                                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {form.site_photos.length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-4">No photos attached yet</p>
                        )}
                      </div>
                    </>
                  )}
                  
                  {/* ========== RESIDENTIAL-RENOVATION SECTIONS ========== */}
                  {isResidentialRenovation && (
                    <>
                      {/* Architectural & Structural Assessment */}
                      <SectionHeader title="Architectural & Structural Assessment" section="res_architectural" />
                      {expandedSections.res_architectural && (
                        <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                          {renderFormField("Slab & Flooring", "res_arch_slab_flooring", "Condition / Observations", true, "Check: Slab thickness, FFL (Finished Floor Level), substrate condition, levelness, cracks")}
                          {renderFormField("Interior Walls", "res_arch_interior_walls", "Condition / Observations", true, "Check: Material (CHB/drywall/concrete), plumb alignment, surface condition")}
                          {renderFormField("Ceiling", "res_arch_ceiling", "Condition / Observations", true, "Check: Ceiling height, slab-to-slab clearance, existing finishes, MEPF routing space")}
                          {renderFormField("Roofing", "res_arch_roofing", "Condition / Observations", true, "Check: Roof type (concrete/tiles/metal), leaks, flashing condition, gutter & downspout, insulation")}
                          {renderFormField("Windows & Glazing", "res_arch_windows", "Condition / Observations", true, "Check: Window type, operability, weatherproofing, glass condition, screen provisions")}
                          {renderFormField("Doors & Frames", "res_arch_doors", "Condition / Observations", true, "Check: Main door, bedroom doors, bathroom doors, hardware condition")}
                          {renderFormField("Balcony/Terrace", "res_arch_balcony", "Condition / Observations", true, "Check: Waterproofing, floor drain, railing height (min 1.0m), structural condition")}
                          {renderFormField("Storage Areas", "res_arch_storage", "Condition / Observations", true, "Check: Built-in cabinet spaces, utility closet, bodega provisions")}
                          {renderFormField("Wet Area Waterproofing", "res_arch_wet_area", "Condition / Observations", true, "Check: Bathroom/kitchen floor membrane, shower area, floor slope to drain")}
                        </div>
                      )}
                      
                      {/* Engineering (MEPF) Assessment */}
                      <SectionHeader title="Engineering (MEPF) Assessment" section="res_mepf" />
                  {expandedSections.res_mepf && (
                    <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                      {renderFormField("Electrical - Main Panel", "res_mepf_main_panel", "Condition / Observations", true, "Check: Panel location, breaker capacity (Amps), available slots, main breaker rating")}
                      {renderFormField("Electrical - Outlets", "res_mepf_outlets", "Condition / Observations", true, "Check: Outlet count per room, GFCI in wet areas, grounding, voltage (220V/110V)")}
                      {renderFormField("Electrical - Lighting", "res_mepf_lighting", "Condition / Observations", true, "Check: Lighting provision points, switch locations, dimmer compatibility")}
                      {renderFormField("Electrical - AC Provision", "res_mepf_ac_provision", "Condition / Observations", true, "Check: AC outlet locations, dedicated circuit, drain provisions, outdoor unit space")}
                      {renderFormField("Plumbing - Water Supply", "res_mepf_water_supply", "Condition / Observations", true, "Check: Water meter location, pipe material, pressure (PSI), shut-off valves")}
                      {renderFormField("Plumbing - Drainage", "res_mepf_drainage", "Condition / Observations", true, "Check: Floor drain locations, P-trap presence, vent stack access")}
                      {renderFormField("Plumbing - Hot Water", "res_mepf_hot_water", "Condition / Observations", true, "Check: Water heater provision, location, electrical/gas requirements")}
                      {renderFormField("HVAC - Ventilation", "res_mepf_ventilation", "Condition / Observations", true, "Check: Exhaust fans, fresh air intake, window ventilation adequacy")}
                      {renderFormField("Fire Protection", "res_mepf_fire_protection", "Condition / Observations", true, "Check: Sprinkler heads (if high-rise), smoke detector, fire extinguisher provision")}
                      {renderFormField("Communications", "res_mepf_communications", "Condition / Observations", true, "Check: CATV/cable outlet, telephone/data points, intercom/doorbell")}
                    </div>
                  )}
                  
                  {/* Room-by-Room Assessment */}
                  <SectionHeader title="Room-by-Room Assessment" section="res_rooms" />
                  {expandedSections.res_rooms && (
                    <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                      {renderFormField("Living/Dining Area", "res_room_living_dining", "Condition / Observations", true, "Check: Natural lighting, ventilation, floor condition, wall condition, ceiling height")}
                      {renderFormField("Kitchen", "res_room_kitchen", "Condition / Observations", true, "Check: Counter space provision, sink rough-in, range hood exhaust, electrical load for appliances")}
                      {renderFormField("Master Bedroom", "res_room_master_bedroom", "Condition / Observations", true, "Check: Room dimensions, closet space, AC provision, window egress")}
                      {renderFormField("Bedroom 2", "res_room_bedroom2", "Condition / Observations", true, "Check: Room dimensions, closet space, AC provision, window")}
                      {renderFormField("Bedroom 3", "res_room_bedroom3", "Condition / Observations", true, "Check: Room dimensions, closet space, AC provision, window")}
                      {renderFormField("Master Bathroom", "res_room_master_bathroom", "Condition / Observations", true, "Check: Toilet rough-in, lavatory rough-in, shower drain, ventilation, waterproofing")}
                      {renderFormField("Common Bathroom", "res_room_common_bathroom", "Condition / Observations", true, "Check: Toilet rough-in, lavatory rough-in, shower/tub provision, ventilation")}
                      {renderFormField("Service/Utility Area", "res_room_service_area", "Condition / Observations", true, "Check: Laundry provisions (washer/dryer), utility sink, floor drain")}
                      {renderFormField("Maid's Room", "res_room_maids_room", "Condition / Observations", true, "Check: Room dimensions, ventilation, T&B provision (if applicable)")}
                    </div>
                  )}
                  
                  {/* Renovation & Salvage Assessment (for Residential-Renovation only) */}
                  {form.category === 'Residential-Renovation' && (
                    <>
                      <SectionHeader title="Renovation & Salvage Assessment" section="res_salvage" />
                      {expandedSections.res_salvage && (
                        <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                          <div className="grid md:grid-cols-2 gap-4">
                            {renderFormField("Existing Flooring", "res_salvage_flooring", "Condition / Recommendation", true, "Can it be retained or refinished?")}
                            {renderFormField("Existing Walls", "res_salvage_walls", "Condition / Recommendation", true, "Load-bearing identification, repainting vs. replastering")}
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            {renderFormField("Existing Ceiling", "res_salvage_ceiling", "Condition / Recommendation", true, "Condition, height, can existing be retained?")}
                            {renderFormField("Existing Fixtures", "res_salvage_fixtures", "Condition / Recommendation", true, "Bathroom fixtures, kitchen sink, faucets - retain or replace?")}
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            {renderFormField("Existing Cabinetry", "res_salvage_cabinetry", "Condition / Recommendation", true, "Kitchen cabinets, closets - retain, reface, or replace?")}
                            {renderFormField("Electrical System", "res_salvage_electrical", "Condition / Recommendation", true, "Panel age, wiring condition (aluminum vs copper), upgrade needed?")}
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            {renderFormField("Plumbing System", "res_salvage_plumbing", "Condition / Recommendation", true, "Pipe material, age, corrosion, replacement needed?")}
                            {renderFormField("Hazardous Materials", "res_salvage_hazardous", "Condition / Recommendation", true, "Lead paint (pre-1978 units), asbestos (popcorn ceiling), mold presence")}
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            {renderFormField("Pest Evidence", "res_salvage_pest", "Condition / Recommendation", true, "Signs of termites, rodents, cockroaches in cabinets/walls")}
                            {renderFormField("Structural Issues", "res_salvage_structural", "Condition / Recommendation", true, "Cracks, water damage, settling, previous modifications")}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Condo/Building Requirements */}
                  <SectionHeader title="Building/Condo Requirements" section="res_condo" />
                  {expandedSections.res_condo && (
                    <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-2">For condominium units - document building-specific requirements</p>
                      <div className="grid md:grid-cols-2 gap-4">
                        {renderFormField("Fit-out Guidelines", "res_condo_fitout_guidelines", "Notes", true, "Does condo have specific fit-out manual? Restrictions?")}
                        {renderFormField("Working Hours", "res_condo_working_hours", "Notes", true, "Allowed construction hours (typically 9AM-5PM weekdays)")}
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        {renderFormField("Contractor Accreditation", "res_condo_contractor_accreditation", "Notes", true, "Does condo require accredited contractors?")}
                        {renderFormField("Permits Required", "res_condo_permits", "Notes", true, "Building permit, electrical permit, plumbing permit")}
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        {renderFormField("Deposit/Bond", "res_condo_deposit_bond", "Notes", true, "Renovation bond amount, refundable conditions")}
                        {renderFormField("Material Delivery", "res_condo_material_delivery", "Notes", true, "Service elevator schedule, delivery hours")}
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        {renderFormField("Debris Disposal", "res_condo_debris_disposal", "Notes", true, "Condo's debris disposal policy, designated area")}
                        {renderFormField("Neighbor Notification", "res_condo_neighbor_notification", "Notes", true, "Required notice period to adjacent units")}
                      </div>
                    </div>
                  )}
                  
                  {/* Other Findings */}
                  <SectionHeader title="Other Findings and Concerns" section="other" />
                  {expandedSections.other && (
                    <div className="p-4 border border-border/40 rounded-lg">
                      {renderFormField("Additional Notes", "other_findings", "Any other findings, concerns, or observations...", true)}
                    </div>
                  )}
                  
                  {/* Required Documents */}
                  <SectionHeader title="Required Documents Checklist" section="res_documents" />
                  {expandedSections.res_documents && (
                    <div className="space-y-3 p-4 border border-border/40 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-4">Mark each document as Available or N/A</p>
                      {[
                        { field: 'res_doc_floor_plan', label: 'Unit Floor Plan' },
                        { field: 'res_doc_asbuilt_mepf', label: 'As-built MEPF Plans' },
                        { field: 'res_doc_fitout_guidelines', label: 'Condo Fit-out Guidelines' },
                        { field: 'res_doc_title_cct', label: 'Title/CCT Copy' },
                        { field: 'res_doc_association_clearance', label: 'Association Clearance' },
                        { field: 'res_doc_renovation_records', label: 'Previous Renovation Records' },
                      ].map(({ field, label }) => (
                        <div key={field} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
                          <span className="text-sm">{label}</span>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant={form[field] === 'Available' ? 'default' : 'outline'}
                              onClick={() => handleInputChange(field, 'Available')}
                              className="text-xs"
                            >
                              Available
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant={form[field] === 'N/A' ? 'default' : 'outline'}
                              onClick={() => handleInputChange(field, 'N/A')}
                              className="text-xs"
                            >
                              N/A
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Final Recommendation */}
                  <SectionHeader title="Final Recommendation" section="final" />
                  {expandedSections.final && (
                    <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                      <div className="space-y-3">
                        <Label>Select Recommendation</Label>
                        <div className="space-y-2">
                          <label className="flex items-start gap-3 p-3 border border-border/40 rounded-lg cursor-pointer hover:bg-green-500/5 transition-colors">
                            <input
                              type="radio"
                              name="res_recommendation"
                              value="feasible"
                              checked={form.final_recommendation === 'feasible'}
                              onChange={(e) => handleInputChange('final_recommendation', e.target.value)}
                              className="mt-1"
                            />
                            <div>
                              <span className="font-medium text-green-500">FEASIBLE FOR FIT-OUT</span>
                              <p className="text-sm text-muted-foreground">Site is suitable for proposed fit-out with no major issues</p>
                            </div>
                          </label>
                          <label className="flex items-start gap-3 p-3 border border-border/40 rounded-lg cursor-pointer hover:bg-yellow-500/5 transition-colors">
                            <input
                              type="radio"
                              name="res_recommendation"
                              value="conditional"
                              checked={form.final_recommendation === 'conditional'}
                              onChange={(e) => handleInputChange('final_recommendation', e.target.value)}
                              className="mt-1"
                            />
                            <div>
                              <span className="font-medium text-yellow-500">FEASIBLE WITH CONDITIONS</span>
                              <p className="text-sm text-muted-foreground">Site is suitable but requires addressing specific issues first</p>
                            </div>
                          </label>
                          <label className="flex items-start gap-3 p-3 border border-border/40 rounded-lg cursor-pointer hover:bg-red-500/5 transition-colors">
                            <input
                              type="radio"
                              name="res_recommendation"
                              value="not_feasible"
                              checked={form.final_recommendation === 'not_feasible'}
                              onChange={(e) => handleInputChange('final_recommendation', e.target.value)}
                              className="mt-1"
                            />
                            <div>
                              <span className="font-medium text-red-500">NOT FEASIBLE</span>
                              <p className="text-sm text-muted-foreground">Significant issues that make fit-out impractical or cost-prohibitive</p>
                            </div>
                          </label>
                        </div>
                      </div>
                      
                      {form.final_recommendation === 'conditional' && (
                        renderFormField("Issues to be Rectified", "conditional_issues", "List the issues that need to be addressed...", true)
                      )}
                    </div>
                  )}
                  
                  {/* Site Photos */}
                  <div className="space-y-4 p-4 border border-border/40 rounded-lg">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2">
                        <Image className="w-4 h-4" />
                        Site Photos
                      </Label>
                      <div>
                        <input
                          type="file"
                          id="tsir-photos-res"
                          multiple
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('tsir-photos-res').click()}
                          disabled={uploadingPhotos}
                        >
                          {uploadingPhotos ? (
                            <>Uploading...</>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Add Photos
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    {form.site_photos.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {form.site_photos.map((url, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={url}
                              alt={`Site photo ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg border border-border/40"
                            />
                            <button
                              type="button"
                              onClick={() => removePhoto(index)}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {form.site_photos.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">No photos attached yet</p>
                    )}
                  </div>
                    </>
                  )}
                  {/* End of Residential-Renovation sections */}
                </>
              )}
              
              {/* Submit Button */}
              {(isRestaurantCategory || isResidentialCategory || !form.category) && (
                <div className="flex gap-3 pt-4">
                  <Button type="submit" data-testid="btn-save-tsir" disabled={!isRestaurantCategory && !isResidentialCategory}>
                    {editingId ? 'Update TSIR' : 'Save TSIR'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }}>
                    Cancel
                  </Button>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      )}
      
      {/* TSIR List */}
      <Card className="glass-effect border-border/40" data-testid="tsir-list-card">
        <CardHeader>
          <CardTitle>TSIR Reports</CardTitle>
        </CardHeader>
        <CardContent>
          {tsirList.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/40">
                    <th className="text-left py-3 px-2 font-medium text-muted-foreground">Project</th>
                    <th className="text-left py-3 px-2 font-medium text-muted-foreground">Category</th>
                    <th className="text-left py-3 px-2 font-medium text-muted-foreground">Recommendation</th>
                    <th className="text-left py-3 px-2 font-medium text-muted-foreground">Date</th>
                    <th className="text-left py-3 px-2 font-medium text-muted-foreground">Created By</th>
                    <th className="text-right py-3 px-2 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tsirList.map((tsir) => (
                    <tr key={tsir.id} className="border-b border-border/20 hover:bg-muted/20" data-testid={`tsir-row-${tsir.id}`}>
                      <td className="py-3 px-2">
                        <p className="font-medium text-foreground">{tsir.project_name}</p>
                        <p className="text-xs text-muted-foreground">{tsir.project_location}</p>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`px-2 py-1 rounded text-xs border ${getCategoryBadgeColor(tsir.category)}`}>
                          {tsir.category?.replace('-', ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`px-2 py-1 rounded text-xs border ${getRecommendationBadge(tsir.final_recommendation)}`}>
                          {tsir.final_recommendation?.replace('_', ' ').toUpperCase() || 'Pending'}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-muted-foreground text-xs">
                        {tsir.inspection_date || new Date(tsir.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-2 text-muted-foreground text-xs">
                        {tsir.created_by_name}
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleView(tsir)}
                            className="p-2 hover:bg-primary/20 rounded text-primary"
                            title="View"
                            data-testid={`btn-view-tsir-${tsir.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(tsir)}
                            className="p-2 hover:bg-blue-500/20 rounded text-blue-500"
                            title="Edit"
                            data-testid={`btn-edit-tsir-${tsir.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleExportPdf(tsir.id)}
                            className="p-2 hover:bg-green-500/20 rounded text-green-500"
                            title="Export PDF"
                            data-testid={`btn-export-tsir-${tsir.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(tsir.id)}
                            className="p-2 hover:bg-red-500/20 rounded text-red-500"
                            title="Delete"
                            data-testid={`btn-delete-tsir-${tsir.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No TSIR reports yet</p>
              <p className="text-sm">Click "New TSIR" to create your first report</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>TSIR Details - {viewingTsir?.project_name}</DialogTitle>
          </DialogHeader>
          {viewingTsir && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                <div><strong>Category:</strong> {viewingTsir.category}</div>
                <div><strong>Location:</strong> {viewingTsir.project_location}</div>
                <div><strong>Inspection Date:</strong> {viewingTsir.inspection_date}</div>
                <div><strong>Lead Architect:</strong> {viewingTsir.lead_architect}</div>
                <div><strong>Lease Space:</strong> {viewingTsir.lease_space}</div>
                <div><strong>GLA:</strong> {viewingTsir.gross_leasable_area}</div>
              </div>
              
              <div className="p-4 border border-border/40 rounded-lg">
                <h4 className="font-semibold mb-2">Final Recommendation</h4>
                <span className={`px-3 py-2 rounded text-sm border ${getRecommendationBadge(viewingTsir.final_recommendation)}`}>
                  {viewingTsir.final_recommendation?.replace('_', ' ').toUpperCase() || 'Not Set'}
                </span>
                {viewingTsir.conditional_issues && (
                  <p className="mt-2 text-muted-foreground">Issues: {viewingTsir.conditional_issues}</p>
                )}
              </div>
              
              {viewingTsir.other_findings && (
                <div className="p-4 border border-border/40 rounded-lg">
                  <h4 className="font-semibold mb-2">Other Findings</h4>
                  <p>{viewingTsir.other_findings}</p>
                </div>
              )}
              
              {viewingTsir.site_photos?.length > 0 && (
                <div className="p-4 border border-border/40 rounded-lg">
                  <h4 className="font-semibold mb-2">Site Photos ({viewingTsir.site_photos.length})</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {viewingTsir.site_photos.map((url, i) => (
                      <img key={i} src={url} alt={`Photo ${i + 1}`} className="w-full h-32 object-cover rounded" />
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 pt-4">
                <Button onClick={() => handleExportPdf(viewingTsir.id)}>
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
                <Button variant="outline" onClick={() => setShowViewDialog(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
